// src/pages/AddProductForm.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../axios";
import {
  Container,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  CircularProgress,
  Typography,
  Divider,
  IconButton,
  Paper,
  Chip,
  Alert,
  Fade,
  useTheme,
  useMediaQuery,
  Tooltip,
  InputAdornment,
  Switch,
  FormControlLabel,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import InventoryIcon from "@mui/icons-material/Inventory";
import CategoryIcon from "@mui/icons-material/Category";
import QrCodeIcon from "@mui/icons-material/QrCode";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ScaleIcon from "@mui/icons-material/Scale";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import StoreIcon from "@mui/icons-material/Store";
import NumbersIcon from "@mui/icons-material/Numbers";
import API_BASE_URL from "../../config";

export default function AddProductForm() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const companyId = Number(localStorage.getItem("companyId") || 0);
  const branchIdFromLogin = Number(localStorage.getItem("branchId") || 0);

  const [formData, setFormData] = useState({
    arabicName: "",
    englishName: "",
    description: "",
    imageUrl: "",
    code: "",
    itemId: 0,
    taxId: 0,
    companyId,
    branchId: branchIdFromLogin,
    categoryId: 0,
    isService: false,
    showInPOS: true,
    units: [],
  });

  const [branches, setBranches] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vatTypes, setVatTypes] = useState([]);
  const [unitDefs, setUnitDefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // دالة لتحديد النص عند التركيز
  const handleFocus = (event) => {
    if (event && event.target) {
      event.target.select();
    }
  };

  // توليد كود المنتج
  const generateProductCode = async (categoryId, branchId, companyId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/Product/max-id?companyId=${companyId}`
      );
      const maxId = response.data || 0;
      const nextItemId = maxId + 1;

      setFormData((prev) => ({
        ...prev,
        itemId: nextItemId,
      }));

      return `${companyId}00${branchId}0${categoryId}000${nextItemId}`;
    } catch (error) {
      console.error("خطأ في توليد كود المنتج:", error);
      return "";
    }
  };

  // تغيير الحقول الرئيسية
  const handleChange = async (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    if (name === "categoryId" && value) {
      const newCode = await generateProductCode(
        value,
        formData.branchId,
        formData.companyId
      );
      setFormData((prev) => ({
        ...prev,
        categoryId: newValue,
        code: newCode,
      }));
    }

    if (name === "branchId" && value && formData.categoryId) {
      const newCode = await generateProductCode(
        formData.categoryId,
        value,
        formData.companyId
      );
      setFormData((prev) => ({
        ...prev,
        branchId: newValue,
        code: newCode,
      }));
    }
  };

  // تحميل البيانات
  useEffect(() => {
    async function fetchData() {
      try {
        const [bRes, cRes, vRes, uRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/Branch/by-company/${companyId}`),
          axios.get(`${API_BASE_URL}/Category/by-company/${companyId}`),
          axios.get(`${API_BASE_URL}/VatTypes?companyId=${companyId}`),
          axios.get(`${API_BASE_URL}/Unit/by-company/${companyId}`),
        ]);

        setBranches(bRes.data);
        setCategories(cRes.data);
        setVatTypes(vRes.data);
        setUnitDefs(uRes.data);

        const defaultBranchId =
          branchIdFromLogin > 0
            ? branchIdFromLogin
            : bRes.data.length > 0
            ? bRes.data[0].id
            : 0;

        const defaultCategoryId = cRes.data.length > 0 ? cRes.data[0].id : 0;

        let defaultCode = "";
        if (defaultCategoryId && defaultBranchId) {
          defaultCode = await generateProductCode(
            defaultCategoryId,
            defaultBranchId,
            companyId
          );
        }

        setFormData((prev) => ({
          ...prev,
          branchId: defaultBranchId,
          categoryId: defaultCategoryId,
          taxId: vRes.data.length > 0 ? vRes.data[0].id : 0,
          code: defaultCode,
          units:
            prev.units.length === 0 && uRes.data.length > 0
              ? [
                  {
                    unitId: uRes.data[0].id,
                    factor: 1,
                    salPrice: 0,
                    purchPrice: 0,
                    barcode: "",
                    isBaseUnit: true,
                  },
                ]
              : prev.units,
        }));
      } catch (err) {
        console.error(err);
        setError("فشل في تحميل البيانات.");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [companyId, branchIdFromLogin]);

  // إضافة وحدة
  const addUnit = () => {
    setFormData((prev) => ({
      ...prev,
      units: [
        ...prev.units,
        {
          unitId: unitDefs.length > 0 ? unitDefs[0].id : 0,
          factor: 1,
          salPrice: 0,
          purchPrice: 0,
          barcode: "",
          isBaseUnit: prev.units.length === 0,
        },
      ],
    }));
  };

  // إزالة وحدة
  const removeUnit = (idxToRemove) => {
    setFormData((prev) => ({
      ...prev,
      units: prev.units.filter((_, idx) => idx !== idxToRemove),
    }));
  };

  // تعديل وحدة
  const handleUnitChange = (idx, field, value) => {
    setFormData((prev) => {
      const units = [...prev.units];
      if (field === "isBaseUnit" && value === true) {
        units.forEach((unit, i) => {
          if (i !== idx) unit.isBaseUnit = false;
        });
      }
      units[idx][field] = value;
      return { ...prev, units };
    });
  };

  // تعديل الباركود
  const handleBarcodeChange = (uIdx, value) => {
    setFormData((prev) => {
      const units = [...prev.units];
      units[uIdx].barcode = value;
      return { ...prev, units };
    });
  };


  // الحفظ
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage('');

    try {
      console.log("🚀 JSON المرسل للسيرفر:", formData);
      await axios.post(`${API_BASE_URL}/Product`, formData);
      setSuccessMessage("✅ تم إضافة المنتج بنجاح!");
      setTimeout(() => {
        navigate("/productList");
      }, 1500);
    } catch (err) {
      console.error("تفاصيل الخطأ:", err.response?.data || err);
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.innerError ||
        err.message;
      setError("❌ حدث خطأ أثناء الحفظ: " + errorMessage);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh" flexDirection="column" gap={2}>
        <CircularProgress size={isMobile ? 40 : 60} />
        <Typography variant={isMobile ? "body1" : "h6"}>جاري التحميل...</Typography>
      </Box>
    );
  }

  return (
    <Container
      maxWidth={false}
      sx={{
        mt: { xs: 2, sm: 3, md: 4 },
        px: { xs: 2, sm: 3, md: 4 },
        pb: { xs: 3, sm: 4, md: 5 }
      }}
      dir="rtl"
    >
      
{/* Header - يعتمد على الثيم */}
<Box
  sx={{
    display: 'flex',
    alignItems: 'center',
    gap: 1.2,
    px: { xs: 1, sm: 2 },
    py: 1.5,
    mb: 2,
    borderBottom: 1,
    borderColor: 'divider',
  }}
>
  <InventoryIcon
    sx={{
      fontSize: 26,
      color: 'primary.main',
    }}
  />

  <Typography
    variant="h6"
    sx={{
      fontWeight: 800,
      color: 'text.primary',
      fontSize: { xs: '1rem', sm: '1.2rem' },
    }}
  >
    إضافة منتج جديد
  </Typography>
</Box>


      {/* رسائل النجاح والخطأ */}
      {successMessage && (
        <Fade in={!!successMessage}>
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccessMessage('')}>
            {successMessage}
          </Alert>
        </Fade>
      )}
      {error && (
        <Fade in={!!error}>
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        </Fade>
      )}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        {/* معلومات المنتج الأساسية */}
        <Paper
          elevation={2}
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            mb: { xs: 3, sm: 4 },
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 3,
              pb: 1.5,
              borderBottom: '2px solid',
              borderColor: 'primary.main',
              fontSize: { xs: '1.1rem', sm: '1.25rem' }
            }}
          >
            <InventoryIcon color="primary" />
            معلومات المنتج الأساسية
          </Typography>

          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="الاسم العربي"
                name="arabicName"
                value={formData.arabicName}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocalOfferIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="الاسم الإنجليزي"
                name="englishName"
                value={formData.englishName}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocalOfferIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="رقم المنتج (ItemId)"
                name="itemId"
                type="number"
                value={formData.itemId}
                onChange={handleChange}
                onFocus={handleFocus}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <NumbersIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="كود المنتج"
                name="code"
                value={formData.code}
                onChange={handleChange}
                onFocus={handleFocus}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <QrCodeIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>المجموعة</InputLabel>
                <Select
                  name="categoryId"
                  value={formData.categoryId}
                  onChange={handleChange}
                  label="المجموعة"
                  sx={{ borderRadius: 2 }}
                >
                  {categories.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CategoryIcon fontSize="small" />
                        {c.nameAr}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>الفرع</InputLabel>
                <Select
                  name="branchId"
                  value={formData.branchId}
                  onChange={handleChange}
                  label="الفرع"
                  sx={{ borderRadius: 2 }}
                >
                  {branches.map((b) => (
                    <MenuItem key={b.id} value={b.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <StoreIcon fontSize="small" />
                        {b.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>نوع الضريبة</InputLabel>
                <Select
                  name="taxId"
                  value={formData.taxId}
                  onChange={handleChange}
                  label="نوع الضريبة"
                  sx={{ borderRadius: 2 }}
                >
                  {vatTypes.map((v) => (
                    <MenuItem key={v.id} value={v.id}>
                      {v.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="الوصف"
                name="description"
                value={formData.description}
                onChange={handleChange}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    name="isService"
                    checked={formData.isService}
                    onChange={handleChange}
                    color="primary"
                  />
                }
                label="خدمة (ليس منتج)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    name="showInPOS"
                    checked={formData.showInPOS}
                    onChange={handleChange}
                    color="success"
                  />
                }
                label="إظهار في نقطة البيع"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* الوحدات والباركود */}
        <Paper
          elevation={2}
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            mb: { xs: 3, sm: 4 },
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
              flexWrap: 'wrap',
              gap: 2
            }}
          >
            <Typography
              variant="h6"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontSize: { xs: '1.1rem', sm: '1.25rem' }
              }}
            >
              <ScaleIcon color="secondary" />
              الوحدات والباركود ({formData.units.length})
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={addUnit}
              sx={{
                borderRadius: 2,
                background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                boxShadow: 3,
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-2px)',
                }
              }}
            >
              إضافة وحدة
            </Button>
          </Box>

          {formData.units.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
              <ScaleIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
              <Typography color="text.secondary">
                لا توجد وحدات. قم بإضافة وحدة أولى للمنتج
              </Typography>
            </Paper>
          ) : (
            formData.units.map((unit, idx) => (
              <Card
                key={idx}
                variant="outlined"
                sx={{
                  mb: 3,
                  p: { xs: 2, sm: 3 },
                  borderRadius: 2,
                  border: '2px solid',
                  borderColor: unit.isBaseUnit ? 'primary.main' : 'divider',
                  boxShadow: unit.isBaseUnit ? 3 : 1,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-2px)',
                  }
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                    flexWrap: 'wrap',
                    gap: 1
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      الوحدة #{idx + 1}
                    </Typography>
                    {unit.isBaseUnit && (
                      <Chip
                        label="وحدة أساسية"
                        color="primary"
                        size="small"
                        icon={<ScaleIcon />}
                      />
                    )}
                  </Box>
                  <Tooltip title="حذف الوحدة">
                    <IconButton
                      color="error"
                      onClick={() => removeUnit(idx)}
                      sx={{
                        border: '1px solid',
                        borderColor: 'error.main',
                        '&:hover': { bgcolor: 'error.light' }
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Grid container spacing={{ xs: 2, sm: 2.5 }}>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>الوحدة</InputLabel>
                      <Select
                        value={unit.unitId}
                        onChange={(e) => handleUnitChange(idx, "unitId", e.target.value)}
                        label="الوحدة"
                        sx={{ borderRadius: 2 }}
                      >
                        {unitDefs.map((u) => (
                          <MenuItem key={u.id} value={u.id}>
                            {u.nameAr}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm={6} md={4}>
                    <TextField
                        fullWidth
                        label="سعر الشراء"
                        type="number"
                        value={unit.purchPrice}
                        onFocus={handleFocus}
                        onChange={(e) => handleUnitChange(idx, "purchPrice", Number(e.target.value))}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AttachMoneyIcon color="success" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={6} md={4}>
                      <TextField
                        fullWidth
                        label="سعر البيع"
                        type="number"
                        value={unit.salPrice}
                        onFocus={handleFocus}
                        onChange={(e) => handleUnitChange(idx, "salPrice", Number(e.target.value))}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AttachMoneyIcon color="error" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={6} md={4}>
                      <TextField
                        fullWidth
                        label="معامل التحويل"
                        type="number"
                        value={unit.factor}
                        onFocus={handleFocus}
                        onChange={(e) => handleUnitChange(idx, "factor", Number(e.target.value))}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      label="الباركود"
                      value={unit.barcode || ""}
                      onChange={(e) => handleBarcodeChange(idx, e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <QrCodeIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'center' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={unit.isBaseUnit}
                            onChange={(e) => handleUnitChange(idx, "isBaseUnit", e.target.checked)}
                            color="primary"
                          />
                        }
                        label="وحدة أساسية"
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Card>
            ))
          )}
        </Paper>

        {/* أزرار الحفظ والإلغاء */}
        <Paper
          elevation={2}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate("/productList")}
              sx={{
                borderRadius: 2,
                px: { xs: 3, sm: 4 },
                py: 1.5,
                minWidth: { xs: '100%', sm: 'auto' }
              }}
            >
              إلغاء
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="large"
              startIcon={<SaveIcon />}
              sx={{
                borderRadius: 2,
                px: { xs: 3, sm: 4 },
                py: 1.5,
                background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                boxShadow: 3,
                minWidth: { xs: '100%', sm: 'auto' },
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-2px)',
                }
              }}
            >
              💾 حفظ المنتج
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}